import { useState, type FormEvent } from 'react';

import type { MfaEnrolementResponse } from '@silex/shared';

import { ApiError, api } from '../api/client';
import { QrCode } from '../components/QrCode';
import { grouperSecretTotp } from '../lib/mfa-format';

import { useAuth } from './AuthContext';

/**
 * Enrôlement du double facteur (AB#296).
 *
 * Montré par `RequireAuth` à la place de toute la console quand le rôle du
 * compte impose le double facteur (`MFA_ROLES_OBLIGATOIRE`) et que le compte
 * ne l'a pas encore enrôlé. L'API refuse alors toutes les routes métier à la
 * session ; cet écran est la seule chose qu'elle permet.
 *
 * Deux temps, comme l'API :
 *  1. « Afficher le QR code » demande un secret neuf. Il n'est rendu qu'une
 *     fois : il n'est pas conservé dans le navigateur, et revenir sur cet
 *     écran en génère un autre (le précédent, jamais confirmé, est remplacé).
 *  2. Le premier code de l'application active le double facteur. L'API révoque
 *     alors toutes les sessions antérieures et en rend une neuve.
 *
 * Le secret est aussi lisible en clair, groupé par quatre : un téléphone sans
 * appareil photo, ou une application installée sur l'ordinateur même, doit
 * pouvoir enrôler.
 */
export function MfaEnrolementScreen() {
  const { user, terminerEnrolementMfa, logout } = useAuth();
  const [enrolement, setEnrolement] = useState<MfaEnrolementResponse | null>(null);
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function demarrer() {
    setEnCours(true);
    setErreur(null);
    try {
      setEnrolement(await api.demarrerEnrolementMfa());
    } catch (err) {
      setErreur(
        err instanceof ApiError
          ? err.message || "L'enrôlement n'a pas pu commencer."
          : 'Connexion impossible. Vérifiez votre connexion réseau.',
      );
    } finally {
      setEnCours(false);
    }
  }

  async function confirmer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnCours(true);
    setErreur(null);
    try {
      terminerEnrolementMfa(await api.confirmerEnrolementMfa(code.trim()));
    } catch (err) {
      setErreur(
        err instanceof ApiError
          ? err.code === 'MFA_REQUIRED'
            ? "Ce code n'est pas le bon. Saisissez celui qu'affiche l'application maintenant."
            : err.message || "L'activation n'a pas abouti."
          : 'Connexion impossible. Vérifiez votre connexion réseau.',
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-card__brand">
          Silex
          <span className="brand-dot" aria-hidden="true" />
        </div>
        <h1 style={{ fontSize: 18, margin: '8px 0' }}>Activez la double authentification</h1>
        <p className="form-note">
          Les comptes {user?.role === 'admin' ? 'administrateur' : 'tuteur'} accèdent aux dossiers
          de plusieurs élèves : un mot de passe seul ne suffit plus. Il vous faut une application
          d'authentification sur votre téléphone (Google Authenticator, Microsoft Authenticator,
          1Password…).
        </p>

        {erreur ? (
          <p className="form-error" role="alert">
            {erreur}
          </p>
        ) : null}

        {!enrolement ? (
          <button type="button" className="btn" onClick={() => void demarrer()} disabled={enCours}>
            {enCours ? 'Préparation…' : 'Afficher le QR code'}
          </button>
        ) : (
          <form onSubmit={(e) => void confirmer(e)} noValidate>
            <p className="form-note">
              1. Dans l'application, ajoutez un compte et scannez ce code.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
              <QrCode
                valeur={enrolement.otpauthUrl}
                libelle="QR code à scanner avec votre application d'authentification"
              />
            </div>
            <p className="form-note">
              Pas d'appareil photo ? Saisissez cette clé à la main :{' '}
              <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>
                {grouperSecretTotp(enrolement.secret)}
              </code>
            </p>

            <div className="field">
              <label htmlFor="enrolement-otp">2. Code à 6 chiffres affiché par l'application</label>
              <input
                id="enrolement-otp"
                type="text"
                name="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <button type="submit" className="btn" disabled={enCours || code.length !== 6}>
              {enCours ? 'Vérification…' : 'Activer'}
            </button>
          </form>
        )}

        <p className="form-note" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
            Se déconnecter
          </button>
        </p>
      </div>
    </div>
  );
}
